import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ROLES, ROLE_LABELS } from '@recica/shared/permissions';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { Table } from '../components/ui/Table.jsx';
import { useTranslation } from '../i18n/useTranslation.js';

import { usePlatformAuth } from './PlatformAuthContext.jsx';
import { tenantUsersApi, tenantsApi } from './platformApi.js';

/**
 * Die Benutzer einer Kundenfirma, verwaltet vom Anbieter.
 *
 * Was hier fehlt und fehlen soll: ein Knopf "als dieser Benutzer anmelden".
 * Den gibt es nicht, weil es dafür keinen Codepfad gibt - der Anbieter
 * verwaltet Firmen, er sieht ihre Zahlen nicht.
 *
 * Auch das Zurücksetzen vergibt kein neues Kennwort, sondern erzeugt einen
 * Einmal-Link. Das Kennwort wählt der Kunde; der Anbieter erfährt es nie.
 */

const EMPTY_NEW = { email: '', password: '', firstName: '', lastName: '', roles: ['sales'] };

export function TenantUsersPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { permissions } = usePlatformAuth();
  const canWrite = permissions?.tenantUsers === 'write';

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW);
  const [error, setError] = useState(null);
  const [resetLink, setResetLink] = useState(null);

  const tenant = useQuery({ queryKey: ['platform', 'tenant', id], queryFn: () => tenantsApi.get(id) });
  const users = useQuery({
    queryKey: ['platform', 'tenantUsers', id],
    queryFn: () => tenantUsersApi.list(id),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['platform', 'tenantUsers', id] });
    queryClient.invalidateQueries({ queryKey: ['platform', 'audit', id] });
  }

  const createUser = useMutation({
    mutationFn: () =>
      tenantUsersApi.create(id, {
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        roles: form.roles,
      }),
    onSuccess: () => {
      refresh();
      setForm(EMPTY_NEW);
      setError(null);
      setCreateOpen(false);
    },
    onError: (err) => setError(err?.message || t('platform.users.create.failed')),
  });

  const toggleActive = useMutation({
    mutationFn: ({ userId, isActive }) => tenantUsersApi.update(id, userId, { isActive }),
    onSuccess: refresh,
    onError: (err) => setError(err?.message || t('platform.users.update.failed')),
  });

  const resetPassword = useMutation({
    mutationFn: (userId) => tenantUsersApi.resetPassword(id, userId),
    onSuccess: (data) => {
      setResetLink(data);
      refresh();
    },
    onError: (err) => setError(err?.message || t('platform.users.reset.failed')),
  });

  const columns = [
    { key: 'email', label: t('common.email'), render: (u) => <span className="mono">{u.email}</span> },
    {
      key: 'name',
      label: t('platform.users.col.name'),
      render: (u) => [u.firstName, u.lastName].filter(Boolean).join(' ') || '-',
    },
    {
      key: 'roles',
      label: t('platform.users.col.roles'),
      render: (u) => (
        <span className="pf-badges">
          {(u.roles || []).map((r) => (
            <Badge key={r} variant="neutral">
              {ROLE_LABELS[r] ? t(ROLE_LABELS[r]) : r}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('platform.users.col.status'),
      render: (u) => (
        <Badge variant={u.isActive ? 'success' : 'danger'}>
          {u.isActive ? t('platform.users.active') : t('platform.users.blocked')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (u) => {
        if (!canWrite) {
          return null;
        }
        return (
          <span className="pf-badges">
            <Button
              size="sm"
              variant="ghost"
              disabled={resetPassword.isPending || !u.isActive}
              onClick={() => resetPassword.mutate(u.id)}
            >
              {t('platform.users.reset.cta')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={toggleActive.isPending}
              onClick={() => toggleActive.mutate({ userId: u.id, isActive: !u.isActive })}
            >
              {u.isActive ? t('platform.users.block') : t('platform.users.unblock')}
            </Button>
          </span>
        );
      },
    },
  ];

  if (users.error) {
    return <div className="alert danger">{users.error.message || t('common.loadError')}</div>;
  }

  return (
    <>
      <div className="pf-page-head">
        <div>
          <p className="muted text-xs" style={{ margin: '0 0 var(--space-1)' }}>
            <Link to="/platform/tenants">{t('platform.nav.tenants')}</Link>
            {' / '}
            <Link to={`/platform/tenants/${id}`}>{tenant.data?.name || '…'}</Link>
          </p>
          <h1>{t('platform.users.title')}</h1>
          <p>{t('platform.users.hint')}</p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('platform.users.create.cta')}
          </Button>
        )}
      </div>

      {error && <div className="alert danger">{error}</div>}

      <Table
        columns={columns}
        rows={users.data || []}
        isLoading={users.isLoading}
        emptyMessage={t('platform.users.empty')}
      />

      {/* Der Einmal-Link wird EINMAL gezeigt und nirgends gespeichert - in der
          Datenbank liegt nur sein Hashwert. */}
      <Modal
        open={Boolean(resetLink)}
        title={t('platform.users.reset.title')}
        onClose={() => setResetLink(null)}
        footer={
          <Button variant="primary" onClick={() => setResetLink(null)}>
            {t('common.close')}
          </Button>
        }
      >
        <p className="text-sm" style={{ marginTop: 0 }}>
          {t('platform.users.reset.explain', { email: resetLink?.email || '' })}
        </p>
        <pre className="pf-reset-link">{resetLink?.url}</pre>
        <p className="muted text-xs">{t('platform.users.reset.once')}</p>
      </Modal>

      <Modal
        open={createOpen}
        title={t('platform.users.create.title')}
        onClose={() => {
          setCreateOpen(false);
          setError(null);
        }}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => createUser.mutate()}
              disabled={
                createUser.isPending ||
                !form.email.trim() ||
                form.password.length < 10 ||
                form.roles.length === 0
              }
            >
              {createUser.isPending ? t('platform.users.create.saving') : t('platform.users.create.cta')}
            </Button>
          </>
        }
      >
        <label className="field">
          <span className="field-label">{t('common.email')}</span>
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            autoFocus
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('platform.tenants.admin.firstName')}</span>
            <input
              className="input"
              value={form.firstName}
              onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">{t('platform.tenants.admin.lastName')}</span>
            <input
              className="input"
              value={form.lastName}
              onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
            />
          </label>
        </div>

        <label className="field">
          <span className="field-label">{t('platform.tenants.admin.password')}</span>
          <input
            className="input"
            type="password"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            minLength={10}
          />
          <span className="muted text-xs">{t('platform.users.create.passwordHint')}</span>
        </label>

        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="field-label">{t('platform.users.col.roles')}</legend>
          <div className="pf-role-grid">
            {ROLES.map((role) => (
              <label key={role} className="pf-role-option">
                <input
                  type="checkbox"
                  checked={form.roles.includes(role)}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      roles: e.target.checked ? [...p.roles, role] : p.roles.filter((r) => r !== role),
                    }))
                  }
                />
                <span>{ROLE_LABELS[role] ? t(ROLE_LABELS[role]) : role}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {error && <div className="alert danger">{error}</div>}
      </Modal>
    </>
  );
}
